import { Chart } from '@common/components/charts';
import { useChartInteractions } from '@common/hooks/useChartInteractions';
import { useFormatedDate, useFormatedVolume } from '@common/hooks';
import * as Tabs from '@radix-ui/react-tabs';
import { Flex, Grid } from '@radix-ui/themes';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTabCard, DataTabCardProps } from './DataTabCard';
import {
    applyChartFormatting,
    groupData,
    RawAssetData,
} from './metricsHelpers';

import styles from './Metrics.module.css';

interface CategoryDistributionProps {
    totalContent: number;
    totalSize: number;
    lastUpdated: Date;
    rawData: RawAssetData[];
}

type TabState = 'count' | 'size';
const DEFAULT_TAB: TabState = 'count';

export const CategoryDistribution = ({
    totalContent,
    totalSize,
    lastUpdated,
    rawData,
}: CategoryDistributionProps) => {
    const [activeMetric, setActiveMetric] = useState<TabState>(DEFAULT_TAB);
    const { t } = useTranslation('dashboard');

    const { formatVolume } = useFormatedVolume();
    const { formatDate } = useFormatedDate();

    const displayData = React.useMemo(() => {
        const otherLabel = t('others');

        const grouped = groupData(
            rawData,
            6,
            (a, b) => b.size - a.size,
            (tail, label) => ({
                name: label,
                count: tail.reduce((sum, item) => sum + item.count, 0),
                size: tail.reduce((sum, item) => sum + item.size, 0),
            }),
            otherLabel,
        );

        return applyChartFormatting(grouped, otherLabel);
    }, [rawData, t]);

    const props = useChartInteractions(displayData);

    const valueFormatter = useCallback(
        (value: string | number) => {
            const num = Number(value);
            if (isNaN(num)) return value;

            return activeMetric === 'size'
                ? formatVolume(num)
                : num.toLocaleString();
        },
        [activeMetric, formatVolume],
    );

    const tabs: Record<TabState, DataTabCardProps<TabState>> = {
        count: {
            value: 'count',
            title: t('totalAssets.title'),
            content: totalContent.toLocaleString(),
        },
        size: {
            value: 'size',
            title: t('totalSpaceUsed.title'),
            content: formatVolume(totalSize),
            subTitle: t(`totalSpaceUsed.subTitle`, {
                lastUpdated: formatDate(lastUpdated),
            }),
        },
    };

    return (
        <Tabs.Root
            className={styles.tabsRoot}
            value={activeMetric}
            onValueChange={(value) => setActiveMetric(value as TabState)}
            orientation='vertical'
            defaultValue={DEFAULT_TAB}
            asChild
        >
            <Grid
                columns={{ md: '250px 1fr' }}
                gap='3'
                width='100%'
                height='100%'
            >
                <Tabs.List asChild>
                    <Flex gap='3' direction='column'>
                        {Object.entries(tabs).map(([key, state]) => (
                            <DataTabCard key={key} {...state} />
                        ))}
                    </Flex>
                </Tabs.List>

                <Chart.Box
                    title={t('assetsDistribution.title')}
                    legend={
                        <Chart.Legend
                            items={displayData}
                            valueKey={activeMetric}
                            valueFormatter={valueFormatter}
                            {...props}
                        />
                    }
                >
                    <Chart.Pie
                        data={displayData}
                        dataKey={activeMetric}
                        valueFormatter={valueFormatter}
                        {...props}
                    />
                </Chart.Box>
            </Grid>
        </Tabs.Root>
    );
};
